import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import { AutoFixNormalOutlinedIcon, Button, Spinner } from 'tuui';
import { INDICATION_TYPES, TOOLTIP_POSITION, TooltipV2 as Tooltip } from 'taboola-ultimate-ui';
import { FEATURE_FLAGS, useConfigMatch } from 'modules/taboola-common-frontend-modules/account-configurations';
import { useFormFieldValue } from 'modules/taboola-common-frontend-modules/formData';
import { GTM_EVENTS, gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import { FormattedMessage } from 'modules/taboola-common-frontend-modules/i18n';
import { useValidationContext } from 'modules/taboola-common-frontend-modules/validations/ValidationContext';
import { COMPONENT_STATUS } from 'services/constants';
import { DEFAULT_ZOOM, ORIGIN_POINT } from '../../constants/croppingConsts';
import { OUTCROPPING_COMPONENT_NAME } from '../../constants/croppingModalGtmConstants';
import { getSanitizedCrop, isNewCropDataSimilarToInitial } from '../../utils/cropDataUtils';
import { CroppingModalWarningBadge } from '../CroppingModalWarningBadge/CroppingModalWarningBadge';
import { OutcroppingGalleryItemDropdown } from '../OutcroppingGalleryItemDropdown/OutcroppingGalleryItemDropdown';
import { CropImageControls } from './CropImageControls';
import styles from './cropImage.module.scss';

const classNameBuilder = classnames.bind(styles);

const OutcropTooltipMessage = ({ replacedImageUrl }) => {
    let message = {
        id: 'cropping.content.outcrop.button.tooltip',
        defaultMessage:
            "We will automatically extend your ad's image using Generative AI without losing vital information. This can help you ensure your image isn't being cropped in any placement's aspect ratio",
    };
    if (replacedImageUrl) {
        message = {
            id: 'cropping.content.outcrop.button.tooltip.replacedImage',
            defaultMessage: 'Please revert your changes by clicking on reset in order to use the Image Extender',
        };
    }

    return <FormattedMessage id={message.id} defaultMessage={message.defaultMessage} />;
};

export const CropImage = ({
    src: originalSrc,
    updateSrc,
    originalSrc: outcroppingOriginalSrc,
    ratio = 16 / 9,
    onOutcropImage,
    srcHistory,
    setSrcHistory,
    outcroppingStatus,
    isOutcroppingEnabled,
    shouldShowOutcropWarning,
    index,
    maxZoom: originalSrcMaxZoom,
    setKey,
    isEqualToInitialData,
}) => {
    const { value: croppedAreaPixels, onChange: setCroppedAreaPixels } = useFormFieldValue({
        field: 'croppedAreaPixels',
    });
    const { value: initialCroppedAreaPixels } = useFormFieldValue({
        field: 'initialCroppedAreaPixels',
    });
    const { value: replacedImageUrl } = useFormFieldValue({
        field: 'replacedImageUrl',
    });
    const { value: replacedImageMaxZoom } = useFormFieldValue({
        field: 'replacedImageMaxZoom',
    });
    const isVerticalRatio = ratio < 1;
    const src = replacedImageUrl || originalSrc;
    const maxZoom = replacedImageMaxZoom || originalSrcMaxZoom;
    const [crop, setCrop] = useState(ORIGIN_POINT);
    const [zoom, setZoom] = useState(DEFAULT_ZOOM);
    const [currentCroppingValue] = useState(croppedAreaPixels || initialCroppedAreaPixels);
    const isRangeOfRatioOutcroppingEnabled = useConfigMatch({
        [FEATURE_FLAGS.RANGE_OF_RATIO_OUTCROPPING_ENABLED]: 'true',
    });

    const shouldSupportBpRatios = useConfigMatch({
        [FEATURE_FLAGS.SUPPORT_BP_ASPECT_RATIOS]: 'true',
    });

    const { validationService } = useValidationContext();

    const failedValidationData = validationService
        .getInvalidFields()
        ?.find(({ fieldId }) => fieldId === `cropData.${index}`)?.failedValidationData;
    const onChangeCrop = newCrop => {
        const sanitizedCrop = getSanitizedCrop(newCrop);
        setCrop(sanitizedCrop);
    };
    const onChangeZoom = newZoom => {
        setZoom(newZoom);
    };

    const { message: errorMessage, indicationType } = failedValidationData || {};

    const onCropComplete = useCallback(
        (_, croppedAreaPixels) => {
            if (
                initialCroppedAreaPixels &&
                isNewCropDataSimilarToInitial(croppedAreaPixels, initialCroppedAreaPixels)
            ) {
                return;
            }
            setCroppedAreaPixels(croppedAreaPixels);
        },
        [setCroppedAreaPixels, initialCroppedAreaPixels]
    );

    const onExtendImageClick = useCallback(() => {
        const onOutcropImageProps = shouldShowOutcropWarning
            ? {}
            : {
                  method: 'RANGE_OF_RATIO',
                  maskRangeOfRatio: shouldSupportBpRatios ? { min: 1, max: 2 } : undefined,
              };

        onOutcropImage(onOutcropImageProps);

        gtmTracker.trackEvent(GTM_EVENTS.USABILITY, {
            component: shouldShowOutcropWarning
                ? `${OUTCROPPING_COMPONENT_NAME}: Extend Image Button Clicked`
                : `${OUTCROPPING_COMPONENT_NAME}: Range of Ratio Button Clicked`,
            value: src,
        });
    }, [onOutcropImage, shouldShowOutcropWarning, shouldSupportBpRatios, src]);

    const showWarningIndication = indicationType === INDICATION_TYPES.WARNING;
    const shouldShowOutcroppingControls = isOutcroppingEnabled && srcHistory?.length > 0;
    // if outcropping is enabled and (the outcropping warning is shown or the range of ratio outcropping is enabled)
    //    OR if the outcropping is currently in progress, show the outcropping button */
    const shouldShowOutcropButton =
        (isOutcroppingEnabled && (shouldShowOutcropWarning || isRangeOfRatioOutcroppingEnabled)) ||
        outcroppingStatus === COMPONENT_STATUS.LOADING;

    const isOutcroppingDisabled = replacedImageUrl || outcroppingStatus === COMPONENT_STATUS.LOADING;
    return (
        <div className={classNameBuilder('container', { compact: shouldShowOutcroppingControls })}>
            <div className={styles['cropper-wrapper']}>
                <Cropper
                    image={src}
                    crop={crop}
                    zoom={zoom}
                    aspect={ratio}
                    onCropChange={onChangeCrop}
                    objectFit="contain"
                    restrictPosition
                    onCropComplete={onCropComplete}
                    onZoomChange={onChangeZoom}
                    classes={{
                        containerClassName: classNameBuilder('crop-container', {
                            warning: showWarningIndication,
                        }),
                    }}
                    maxZoom={maxZoom}
                    initialCroppedAreaPixels={currentCroppingValue}
                />
                {(showWarningIndication || shouldShowOutcropWarning) && (
                    <CroppingModalWarningBadge
                        shouldShowOutcropWarning={shouldShowOutcropWarning}
                        errorMessage={errorMessage}
                    />
                )}
                {shouldShowOutcropButton && !isVerticalRatio && (
                    <Button
                        onClick={onExtendImageClick}
                        className={styles['outcrop-button']}
                        disabled={isOutcroppingDisabled}
                        data-testid="cropping-content-outcrop-button"
                    >
                        <Tooltip position={TOOLTIP_POSITION.TOP}>
                            <OutcropTooltipMessage replacedImageUrl={replacedImageUrl} />
                        </Tooltip>
                        {outcroppingStatus === COMPONENT_STATUS.LOADING ? (
                            <Spinner size={20} className={styles['spinner']} color="info" />
                        ) : (
                            <>
                                <div className={styles['outcrop-icon']}>
                                    <AutoFixNormalOutlinedIcon sx={{ transform: 'none' }} fontSize="small" />
                                </div>
                                <FormattedMessage id="cropping.content.outcrop.button" defaultMessage="Extend Image" />
                            </>
                        )}
                    </Button>
                )}
            </div>
            {shouldShowOutcroppingControls && (
                <div className={styles['dropdown-container']} data-testid="outcropping-dropdown">
                    <OutcroppingGalleryItemDropdown itemUrl={src} />
                </div>
            )}
            <CropImageControls
                src={src}
                ratio={ratio}
                updateSrc={updateSrc}
                originalSrc={outcroppingOriginalSrc}
                srcHistory={srcHistory}
                setSrcHistory={setSrcHistory}
                isOutcroppingEnabled={isOutcroppingEnabled}
                shouldShowOutcroppingControls={shouldShowOutcroppingControls}
                maxZoom={maxZoom}
                zoom={zoom}
                setZoom={setZoom}
                setKey={setKey}
                isEqualToInitialData={isEqualToInitialData}
            />
        </div>
    );
};

CropImage.propTypes = {
    src: PropTypes.string,
};
